// src/data/unionCalendar.js
//
// SECTION 531, RULING 264 (Bob, 2026-09-30). The union calendar. Bob chose option 1 - "it matches how the union actually
// works; each item is accountable to a named officer, and it grows as more officers come onto the platform" - and set
// the terms: "GCs need the flexibility to decline, change out and back in, and not repost or share." And earlier: the
// administrator posts nothing ("No. Not going to work.").
//
// WHO POSTS, by office, for his own committee only:
//   Local Chairperson    - local events, for a local he holds (token scopes)
//   General Chairperson  - any level, for his committee
//   a liaison the GC names (calendarLiaisons/{committee}) - international, legislative or public-relations items, as
//                          named per level. An International item needs its official source link.
//   the administrator    - nothing.
// WHAT THE GC CONTROLS: every international / legislative / public-relations item that is not his own can be declined,
// and switched off and back on, each switch recorded (calendarChoices/{committee}/items/{eventId}). Nothing is ever
// copied, reposted or shared: an item exists once, shown to the audience its author set, less what the GC declined.
// DEADLINES appear by themselves from the claims the person can already see - never typed in.
// Pure module: the page draws and writes, the rules hold the same line server-side.
import { getClaimRequirements } from './claimRequirements';
import { carrierLimits } from './carrierAnswerLimits';
import { lastDayOf } from './timeLimit';   // ruling 314: day one is the day of the event
import { appealDeadlines } from './claimClocks';   // section 539: the appeal ladder's clocks
import { claimActs } from './offProperty';   // section 544

const F = Object.freeze;
export const LEVELS = F([
  { key: 'local', label: 'Local' },
  { key: 'committee', label: 'General Committee' },
  { key: 'international', label: 'International' },
  { key: 'legislative', label: 'Legislative' },
  { key: 'public_relations', label: 'Public relations' },
]);
export const LEVEL_KEYS = F(LEVELS.map(l => l.key));
export const CURATED = F(['international', 'legislative', 'public_relations']);   // the GC may decline these
export const SOURCE_REQUIRED = F(['international']);
export const TITLE_MAX = 140;
export const TEXT_MAX = 2000;
export const levelLabel = (k) => (LEVELS.find(l => l.key === k) || {}).label || k;

const s = (v) => (v == null ? '' : String(v).trim());
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v + 'T00:00:00Z'));
const isTime = (v) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
function httpsUrl(v) { try { const u = new URL(v); return u.protocol === 'https:' ? u.toString() : null; } catch (e) { return null; } }

// The levels this person may post for his committee. liaisons: calendarLiaisons/{committee} data.
export function postableLevels(user, liaisons) {
  if (!user || !user.uid || !user.committee || user.isAdmin) return [];
  if (user.role === 'general_chair') return LEVEL_KEYS.slice();
  const out = [];
  if (user.role === 'local_chair' && Array.isArray(user.scopes) && user.scopes.length) out.push('local');
  for (const k of CURATED) if (liaisons && Array.isArray(liaisons[k]) && liaisons[k].includes(user.uid)) out.push(k);
  return out;
}

export function officeLabel(user, level) {
  if (!user) return '';
  if (user.role === 'general_chair') return 'General Chairperson, ' + user.committee;
  if (level === 'local' && user.role === 'local_chair') return 'Local Chairperson';
  return levelLabel(level) + ' liaison, ' + user.committee;
}

// Build the record for a new event, or say why not.
export function buildEvent(user, input, liaisons, nowIso) {
  const i = input || {};
  const errors = [];
  const level = s(i.level);
  if (!postableLevels(user, liaisons).includes(level)) errors.push('Your office does not post ' + levelLabel(level || '?') + ' items.');
  const title = s(i.title);
  if (!title) errors.push('An item has a title.');
  if (title.length > TITLE_MAX) errors.push('The title is limited to ' + TITLE_MAX + ' characters.');
  const date = s(i.date);
  if (!isDate(date)) errors.push('The date must be a date.');
  const time = s(i.time);
  if (time && !isTime(time)) errors.push('The time must be HH:MM.');
  const description = s(i.description);
  if (description.length > TEXT_MAX) errors.push('The description is limited to ' + TEXT_MAX + ' characters.');
  const sourceUrl = s(i.sourceUrl) ? httpsUrl(s(i.sourceUrl)) : null;
  if (s(i.sourceUrl) && !sourceUrl) errors.push('The source link must be a full https:// address.');
  if (SOURCE_REQUIRED.includes(level) && !sourceUrl) errors.push('An International item needs the link to its official notice.');
  const meetingUrl = s(i.meetingUrl) ? httpsUrl(s(i.meetingUrl)) : null;
  if (s(i.meetingUrl) && !meetingUrl) errors.push('The meeting link must be a full https:// address.');
  const scopeKey = level === 'local' ? s(i.scopeKey) : '';
  if (level === 'local' && !(user && Array.isArray(user.scopes) && user.scopes.includes(scopeKey))) errors.push('Choose one of your locals.');
  if (errors.length) return { ok: false, errors, record: null };
  return { ok: true, errors: [], record: {
    title, date, time: time || null, location: s(i.location) || null, description: description || null,
    level, scopeKey: scopeKey || null, audienceCommittees: [user.committee],
    sourceUrl, meetingUrl, status: 'active',
    postedBy: user.uid, postedByName: user.name || user.email || '', postedByOffice: officeLabel(user, level),
    postedAt: nowIso || new Date().toISOString(),
  } };
}

// Is this item one the GC of `committee` may decline? Curated levels, not posted by himself.
export function isCurated(ev, gcUid) { return !!ev && CURATED.includes(ev.level) && ev.postedBy !== gcUid; }

// The GC's switch for one item: off (declined) or on, with the history of every switch.
export function choicePatch(user, committee, current, show, nowIso) {
  if (!user || user.role !== 'general_chair' || user.committee !== committee) return { ok: false, errors: ['Only the General Chairperson of ' + committee + ' decides what reaches the committee.'] };
  const at = nowIso || new Date().toISOString();
  const history = (current && Array.isArray(current.history) ? current.history : []).concat([{ shown: !!show, by: user.uid, byName: user.name || '', at }]);
  return { ok: true, errors: [], record: { shown: !!show, by: user.uid, at, history } };
}

// What a person sees: active items for his committee, less the ones his GC switched off. choices: { [eventId]: {shown} }.
export function visibleEvents(events, choices, user) {
  const c = user && user.committee;
  return (events || []).filter(e => e && e.status === 'active'
    && (user && user.isAdmin ? true : Array.isArray(e.audienceCommittees) && e.audienceCommittees.includes(c))
    && !(isCurated(e, null) && choices && choices[e.id] && choices[e.id].shown === false))
    .sort((a, b) => (a.date + (a.time || '')).localeCompare(b.date + (b.time || '')));
}

// Deadlines, from claims the person can already see. Nothing typed in; each carries where it came from.
export function deadlinesFromClaims(claims) {
  const out = [];
  for (const c of claims || []) {
    if (!c) continue;
    const label = s(c.id) || s(c.title) || 'claim';
    const req = getClaimRequirements(c.applicableAgreement);
    if (c.status === 'draft' && isDate(s(c.incidentDate)) && req && Number.isFinite(req.filingWindowDays)) {
      out.push({ kind: 'deadline', date: lastDayOf(s(c.incidentDate), req.filingWindowDays),   /* ruling 314 */ title: 'Present claim ' + label + ' (filing window, ' + req.filingWindowDays + ' days from the occurrence)', claimId: c.docId || c.id });
    }
    const lim = carrierLimits(c.applicableAgreement);
    // Section 544: an answer recorded as an act on the ladder ends this clock too (the War Room's Clocks found it running on).
    if (isDate(s(c.presentedOn)) && Number.isFinite(lim.carrierAnswerDays) && !c.carrierAnsweredOn && !c.outcome && !claimActs(c).some(a => a && a.kind === 'carrier_answered')) {
      out.push({ kind: 'deadline', date: lastDayOf(s(c.presentedOn), lim.carrierAnswerDays),   /* ruling 314 */ title: 'Carrier answer due on claim ' + label + ' (' + lim.carrierAnswerDays + ' days from service)', claimId: c.docId || c.id });
    }
    appealDeadlines(c).forEach(d => out.push(d));   // section 539
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}
